import * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * Sober DS (v2) — Pagination. Mirrors the `.pagination` / `.pager` showcase
 * spec: info text fs-12 secondary on the left, 34×34 outline pager buttons on
 * the right (active = primary fill, no colored border; disabled = 40% opacity).
 */
const pagerBtn =
  'inline-grid place-items-center w-[34px] h-[34px] rounded-so-sm border border-sober-outline bg-sober-surface text-so-14 text-sober-secondary transition-colors hover:bg-sober-app focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sober-primary-fg disabled:opacity-40 disabled:pointer-events-none aria-[current=page]:bg-sober-primary-fg aria-[current=page]:text-white aria-[current=page]:border-transparent';

type PageItem = number | 'ellipsis-start' | 'ellipsis-end';

function getPageItems(page: number, pageCount: number): PageItem[] {
  if (pageCount <= 7) {
    return Array.from({ length: pageCount }, (_, i) => i + 1);
  }

  const safePage = Math.min(Math.max(1, page), pageCount);
  const start = Math.max(2, safePage - 1);
  const end = Math.min(pageCount - 1, safePage + 1);
  const items: PageItem[] = [1];

  if (start > 2) items.push('ellipsis-start');
  for (let p = start; p <= end; p += 1) items.push(p);
  if (end < pageCount - 1) items.push('ellipsis-end');

  items.push(pageCount);
  return items;
}

export interface PaginationProps extends React.HTMLAttributes<HTMLDivElement> {
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
  total?: number;
  pageSize?: number;
}

export const Pagination = React.forwardRef<HTMLDivElement, PaginationProps>(
  ({ className, page, pageCount, onPageChange, total, pageSize, ...props }, ref) => {
    const safeCount = Math.max(1, pageCount);
    const safePage = Math.min(Math.max(1, page), safeCount);
    const pageItems = getPageItems(safePage, safeCount);
    const info =
      total != null && pageSize != null
        ? `${total === 0 ? 0 : (safePage - 1) * pageSize + 1}–${Math.min(safePage * pageSize, total)} de ${total}`
        : `Página ${safePage} de ${safeCount}`;

    const go = (next: number) => {
      const clamped = Math.min(Math.max(1, next), safeCount);
      if (clamped !== safePage) onPageChange(clamped);
    };

    return (
      <div
        ref={ref}
        className={cn('flex items-center justify-between gap-3 py-4 flex-wrap', className)}
        {...props}
      >
        <span className="text-so-12 text-sober-secondary">{info}</span>
        <nav className="flex max-w-full flex-wrap items-center justify-end gap-1" aria-label="Paginação">
          <button
            type="button"
            className={pagerBtn}
            disabled={safePage <= 1}
            aria-label="Página anterior"
            onClick={() => go(safePage - 1)}
          >
            ‹
          </button>
          {pageItems.map((item) =>
            typeof item === 'number' ? (
              <button
                key={item}
                type="button"
                className={pagerBtn}
                aria-current={item === safePage ? 'page' : undefined}
                aria-label={`Página ${item}`}
                onClick={() => go(item)}
              >
                {item}
              </button>
            ) : (
              <span
                key={item}
                aria-hidden="true"
                className="inline-grid h-[34px] w-[22px] place-items-center text-so-14 font-bold text-sober-secondary"
              >
                …
              </span>
            )
          )}
          <button
            type="button"
            className={pagerBtn}
            disabled={safePage >= safeCount}
            aria-label="Próxima página"
            onClick={() => go(safePage + 1)}
          >
            ›
          </button>
        </nav>
      </div>
    );
  }
);
Pagination.displayName = 'Pagination';
